import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

export interface SupportSettings {
  app_id?: string; // present in some GET responses
  support_number: string | null;
  support_time: string | null;
  whatsapp_number: string | null;
  whatsapp_enabled: boolean;
  telegram_link: string | null;
  telegram_enabled: boolean;
  maintenance_mode_enabled: boolean;
  withdrawals_enabled?: boolean;
  terms_and_conditions: string | null;
  withdrawal_condition: string | null;
  running_text: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface SupportState {
  settings: SupportSettings | null;
  loading: boolean;
  error: string | null;
  success: string | null;
}

const initialState: SupportState = {
  settings: null,
  loading: false,
  error: null,
  success: null,
};

// Fetch current settings from homepage endpoint
export const fetchSupportSettings = createAsyncThunk(
  'support/fetch',
  async (appId: string, { rejectWithValue }) => {
    try {
      const res = await api.get<any[]>(`/homepage/?app_id=${appId}`);
      // Extract support_settings from config_json
      if (res.data && res.data.length > 0 && res.data[0].config_json?.support_settings) {
        const supportSettings = res.data[0].config_json.support_settings;
        return {
          success: true,
          data: supportSettings as SupportSettings
        };
      }
      return rejectWithValue('No support settings found');
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

// PUT update (full payload)
export const updateSupportSettingsPut = createAsyncThunk(
  'support/updatePut',
  async (
    args: {
      appId: string;
      payload: {
        whatsapp_number?: string | null;
        support_number?: string | null;
        support_time?: string | null;
        whatsapp_enabled?: boolean;
        telegram_enabled?: boolean;
        telegram_link?: string | null;
        maintenance_mode_enabled?: boolean;
        terms_and_conditions?: string | null;
        withdrawal_condition?: string | null;
        running_text?: string | null;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.put<ApiResponse<SupportSettings>>(
        `/apps/${args.appId}/support-settings/`,
        args.payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

// PATCH update (partial)
export const updateSupportSettingsPatch = createAsyncThunk(
  'support/updatePatch',
  async (
    args: {
      appId: string;
      payload: Partial<SupportSettings>;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.patch<ApiResponse<SupportSettings>>(
        `/apps/${args.appId}/support-settings/`,
        args.payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e.response?.data?.detail || e.message);
    }
  }
);

const slice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    clearSupportError(state) {
      state.error = null;
    },
    clearSupportSuccess(state) {
      state.success = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSupportSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSupportSettings.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as ApiResponse<SupportSettings>;
        state.settings = payload.data;
      })
      .addCase(fetchSupportSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // PUT
      .addCase(updateSupportSettingsPut.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateSupportSettingsPut.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as ApiResponse<SupportSettings>;
        state.settings = payload.data;
        state.success = 'Support settings updated successfully';
      })
      .addCase(updateSupportSettingsPut.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // PATCH
      .addCase(updateSupportSettingsPatch.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateSupportSettingsPatch.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as ApiResponse<SupportSettings>;
        state.settings = payload.data;
        state.success = 'Support settings updated successfully';
      })
      .addCase(updateSupportSettingsPatch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearSupportError, clearSupportSuccess } = slice.actions;
export default slice.reducer;


